import { useCallback } from 'react';

import {
  CalendarIcon,
  ClockIcon,
  GlobeIcon,
  ZapIcon,
  InfoIcon,
  ChartNoAxesCombinedIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { selectCampaignId } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import useSegments from '@/segment/useSegments';
import { EventName, PageName } from '@/shared/types/segment';

import { ReviewSummary } from './components/ReviewSummary';
import { SummaryItemSkeleton, TextSkeleton } from '../../components/CampaignLoadingState';
import { useAgenticReviewLogic } from '../../hooks/useAgenticReviewLogic';
import { useRegisterWizardStep } from '../../hooks/useWizardStepController';
import { COMMON_TIMEZONES, formatCampaignDate, getScheduledTimeMessage } from '../../utils';

/**
 * Agentic campaign Review page - displays a summary of campaign settings and lets
 * users configure start/end times before launching. Rendered (via the unified Review
 * delegator) whenever a campaign is agentic, regardless of its type (Outreach or
 * Payment Recovery).
 * @returns JSX element for the agentic campaign review page
 */
export const AgenticReview = () => {
  const { t } = useTranslation();
  const campaignId = useSelector(selectCampaignId);

  const {
    // Form
    form,
    startScheduleType,
    startDate,
    startTime,
    timezone,
    doNotEnd,
    endDate,
    endTime,

    // Dialog state
    isConfirmDialogOpen,
    setIsConfirmDialogOpen,

    // Redux state
    draftAiAgentData,

    // Derived data
    campaignType,
    isNBA,
    audienceStats,
    campaign_senders,

    // Loading state
    isLoadingEssentialData,

    // Handlers
    handleDoNotEndToggle,
    handleStartScheduleTypeChange,
    handleSaveAndExit,
    handleReviewAndStart,
    handleConfirmSubmit,
  } = useAgenticReviewLogic();

  useSegments(PageName.CAMPAIGN_REVIEW);

  const handleTrackedReviewAndStart = useCallback(async () => {
    // Track only once validation passes and the confirm dialog actually opens, so a
    // blocked (invalid-schedule) attempt doesn't inflate the funnel.
    const didOpenConfirm = await handleReviewAndStart();
    if (!didOpenConfirm) {
      return;
    }

    trackEvent(EventName.CampaignReviewAndStart, {
      campaign_id: campaignId,
      campaign_type: 'payment_recovery',
      is_scheduled: startScheduleType === 'custom',
      subscribers_count: audienceStats?.total_subscribers || 0,
      channel_type: campaign_senders?.[0]?.integration_type || '',
    });
  }, [campaignId, startScheduleType, audienceStats, campaign_senders, handleReviewAndStart]);

  const handleTrackedConfirmSubmit = useCallback(() => {
    trackEvent(EventName.CampaignConfirmLaunch, {
      campaign_id: campaignId,
      campaign_type: 'payment_recovery',
      is_scheduled: startScheduleType === 'custom',
    });
    handleConfirmSubmit();
  }, [campaignId, startScheduleType, handleConfirmSubmit]);

  // The wizard "Review and start" CTA validates scheduling + opens the launch
  // dialog (handleReviewAndStart already does both, with tracking).
  useRegisterWizardStep(
    { complete: handleTrackedReviewAndStart, saveAndExit: handleSaveAndExit },
    isLoadingEssentialData
  );

  const {
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = form;

  // Show loading state while essential data is loading
  if (isLoadingEssentialData) {
    return (
      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Summary Section */}
        <div className="flex-1 space-y-6">
          <div className="p-6">
            <h3 className="mb-4 text-lg font-semibold">Summary</h3>

            <div className="space-y-4">
              {/* Audience */}
              <SummaryItemSkeleton />

              {/* Channel */}
              <SummaryItemSkeleton />

              {/* If not delivered */}
              <SummaryItemSkeleton />

              {/* Response handling */}
              <SummaryItemSkeleton />
            </div>
          </div>
        </div>

        {/* Sending Options Section */}
        <div className="w-full space-y-6 lg:w-125">
          <div className="rounded-lg border border-border bg-background p-8">
            <h3 className="mb-4 text-lg font-semibold">{t('campaigns.review.sending_options')}</h3>

            <div className="space-y-4">
              {/* Date, Time skeletons */}
              <div className="space-y-4">
                <div>
                  <TextSkeleton className="mb-2 h-4 w-16" />
                  <div className="h-10 w-full rounded-md bg-muted" />
                </div>
                <div>
                  <TextSkeleton className="mb-2 h-4 w-16" />
                  <div className="h-10 w-full rounded-md bg-muted" />
                </div>
              </div>
            </div>
          </div>

          {/* Review and start button skeleton */}
          <div className="h-10 w-full rounded-md bg-muted" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-4 rounded-xl bg-muted p-1 lg:flex-row">
        {/* Summary Section */}
        <ReviewSummary
          campaignType={campaignType || 'payment_recovery'}
          subscriberCount={audienceStats?.total_subscribers || 0}
          campaignSenders={campaign_senders}
          aiAgentName={draftAiAgentData?.brain_name}
          aiAgentVersion={draftAiAgentData?.brain_version}
          isNBA={isNBA}
        />

        {/* Sending Options Section */}
        <div className="w-full lg:w-125">
          <div className="rounded-lg border border-border bg-background p-8">
            <h3 className="mb-6 text-lg font-semibold">{t('campaigns.review.sending_options')}</h3>

            <Form {...form}>
              <div className="space-y-6">
                {/* Start Section */}
                <div className="space-y-3">
                  <span className="block text-sm font-medium">
                    {t('campaigns.paymentrecoveryreview.start')}
                  </span>

                  {/* Start Schedule Type Dropdown */}
                  <FormField
                    control={control}
                    name="startScheduleType"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Select
                            value={field.value}
                            onValueChange={(value: 'immediately' | 'custom') => {
                              field.onChange(value);
                              handleStartScheduleTypeChange(value);
                            }}
                          >
                            <SelectTrigger id="start-schedule-type" className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="immediately">
                                {t('campaigns.paymentrecoveryreview.immediately')}
                              </SelectItem>
                              <SelectItem value="custom">
                                {t('campaigns.paymentrecoveryreview.custom')}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Date, Time, Timezone - only show when custom */}
                  {startScheduleType === 'custom' && (
                    <>
                      <div className="mb-2 flex gap-3">
                        {/* Start Date */}
                        <FormField
                          control={control}
                          name="startDate"
                          render={({ field }) => (
                            <FormItem className="w-35">
                              <FormControl>
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button
                                      id="start-date-picker"
                                      variant="outline-neutral"
                                      className="w-full gap-2 px-3 font-normal hover:bg-transparent"
                                    >
                                      <span className="min-w-0 flex-1 truncate text-left">
                                        {field.value
                                          ? formatCampaignDate(field.value)
                                          : t('campaigns.paymentrecoveryreview.select_date')}
                                      </span>
                                      <CalendarIcon className="h-4 w-4 shrink-0" />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar
                                      mode="single"
                                      selected={field.value}
                                      onSelect={(date) => {
                                        field.onChange(date);
                                        setValue('startDate', date);
                                        clearErrors('startDate');
                                      }}
                                      disabled={(date) => {
                                        const today = new Date();
                                        today.setHours(0, 0, 0, 0);
                                        const compareDate = new Date(date);
                                        compareDate.setHours(0, 0, 0, 0);
                                        return compareDate < today;
                                      }}
                                    />
                                  </PopoverContent>
                                </Popover>
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        {/* Start Time */}
                        <FormField
                          control={control}
                          name="startTime"
                          render={({ field }) => (
                            <FormItem className="w-35">
                              <FormControl>
                                <div className="relative">
                                  <Input
                                    id="start-time-input"
                                    type="time"
                                    {...field}
                                    onChange={(e) => {
                                      field.onChange(e);
                                      clearErrors('startTime');
                                    }}
                                    className="pr-10"
                                  />
                                  <ClockIcon className="pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                </div>
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        {/* Timezone */}
                        <FormField
                          control={control}
                          name="timezone"
                          render={({ field }) => (
                            <FormItem className="w-35">
                              <FormControl>
                                <Select
                                  value={field.value}
                                  onValueChange={(value) => {
                                    field.onChange(value);
                                    setValue('timezone', value);
                                    clearErrors('timezone');
                                  }}
                                >
                                  <SelectTrigger
                                    id="timezone-select"
                                    className="w-full min-w-0 gap-2 [&>span]:min-w-0 [&>span]:flex-1 [&>span]:truncate [&>span]:text-left [&>svg]:hidden"
                                  >
                                    <SelectValue />
                                    <GlobeIcon className="block! h-4 w-4 shrink-0" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {COMMON_TIMEZONES.map((tz) => (
                                      <SelectItem key={tz.value} value={tz.value}>
                                        {tz.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                      {/* Single error message for start fields */}
                      {(errors.startDate || errors.startTime || errors.timezone) && (
                        <p className="text-center text-sm text-warning">
                          {startDate && startTime && timezone && errors.startTime?.message
                            ? errors.startTime.message
                            : t('campaigns.paymentrecoveryreview.all_fields_required')}
                        </p>
                      )}
                    </>
                  )}
                </div>

                {/* End Section */}
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-medium">
                      {t('campaigns.paymentrecoveryreview.end')}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        {t('campaigns.paymentrecoveryreview.do_not_end')}
                      </span>
                      <Switch checked={doNotEnd} onCheckedChange={handleDoNotEndToggle} />
                    </div>
                  </div>

                  {/* End Date, Time, and Timezone - shown when doNotEnd is false */}
                  {!doNotEnd && (
                    <>
                      <div className="mb-2 flex gap-3">
                        {/* End Date */}
                        <FormField
                          control={control}
                          name="endDate"
                          render={({ field }) => (
                            <FormItem className="w-35">
                              <FormControl>
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button
                                      id="end-date-picker"
                                      variant="outline-neutral"
                                      className="w-full gap-2 px-3 font-normal hover:bg-transparent"
                                    >
                                      <span className="min-w-0 flex-1 truncate text-left">
                                        {field.value
                                          ? formatCampaignDate(field.value)
                                          : t('campaigns.paymentrecoveryreview.select_date')}
                                      </span>
                                      <CalendarIcon className="h-4 w-4 shrink-0" />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar
                                      mode="single"
                                      selected={field.value}
                                      onSelect={(date) => {
                                        field.onChange(date);
                                        setValue('endDate', date);
                                        clearErrors('endDate');
                                      }}
                                      disabled={(date) => {
                                        // End date must be after start date
                                        const minDate = startDate || new Date();
                                        minDate.setHours(0, 0, 0, 0);
                                        const compareDate = new Date(date);
                                        compareDate.setHours(0, 0, 0, 0);
                                        return compareDate < minDate;
                                      }}
                                    />
                                  </PopoverContent>
                                </Popover>
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        {/* End Time */}
                        <FormField
                          control={control}
                          name="endTime"
                          render={({ field }) => (
                            <FormItem className="w-35">
                              <FormControl>
                                <div className="relative">
                                  <Input
                                    id="end-time-input"
                                    type="time"
                                    {...field}
                                    onChange={(e) => {
                                      field.onChange(e);
                                      clearErrors('endTime');
                                    }}
                                    className="pr-10"
                                  />
                                  <ClockIcon className="pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                </div>
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        {/* Timezone - only show in end section when start is 'immediately' */}
                        {startScheduleType === 'immediately' && (
                          <FormField
                            control={control}
                            name="timezone"
                            render={({ field }) => (
                              <FormItem className="w-35">
                                <FormControl>
                                  <Select
                                    value={field.value}
                                    onValueChange={(value) => {
                                      field.onChange(value);
                                      setValue('timezone', value);
                                      clearErrors('timezone');
                                    }}
                                  >
                                    <SelectTrigger
                                      id="end-timezone-select"
                                      className="w-full min-w-0 gap-2 [&>span]:min-w-0 [&>span]:flex-1 [&>span]:truncate [&>span]:text-left [&>svg]:hidden"
                                    >
                                      <SelectValue />
                                      <GlobeIcon className="block! h-4 w-4 shrink-0" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {COMMON_TIMEZONES.map((tz) => (
                                        <SelectItem key={tz.value} value={tz.value}>
                                          {tz.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </FormControl>
                              </FormItem>
                            )}
                          />
                        )}
                      </div>
                      {/* Single error message for end fields */}
                      {(errors.endDate || errors.endTime) && (
                        <p className="text-center text-sm text-warning">
                          {endDate && endTime && errors.endDate?.message
                            ? errors.endDate.message
                            : t('campaigns.paymentrecoveryreview.all_fields_required')}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </Form>

            {/* Campaign start info - only show when custom schedule */}
            {startScheduleType === 'custom' &&
              getScheduledTimeMessage(true, startDate, startTime, timezone) && (
                <div className="mt-4 px-3 py-2 text-center">
                  <p className="text-sm leading-5 text-muted-foreground">
                    <span>{t('campaigns.review.send_time')}:</span>{' '}
                    <span className="font-medium">
                      {getScheduledTimeMessage(true, startDate, startTime, timezone)}
                    </span>{' '}
                    <span>{t('campaigns.review.from_now')}</span>
                  </p>
                </div>
              )}
          </div>

          {/* Info text about campaign end */}
          {!doNotEnd && (
            <p className="mt-4 text-center text-sm text-muted-foreground">
              {t('campaigns.paymentrecoveryreview.end_info')}
            </p>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">
              {t('campaigns.review.dialog.title')}
            </DialogTitle>
          </DialogHeader>

          <DialogDescription asChild>
            <>
              <div className="space-y-4 px-4">
                {/* Scheduled time info - different message for immediate vs custom */}
                {startScheduleType === 'immediately' ? (
                  <div className="flex items-center gap-3">
                    <ClockIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <p className="text-sm text-foreground">
                      {t('campaigns.review.dialog.immediate_message')}
                    </p>
                  </div>
                ) : (
                  startDate &&
                  startTime && (
                    <div className="flex items-center gap-3">
                      <ClockIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <p className="text-sm text-foreground">
                        {t('campaigns.review.dialog.scheduled_message', {
                          0: startTime,
                          1: formatCampaignDate(startDate),
                        })}
                      </p>
                    </div>
                  )
                )}

                {/* End time info (if "Do not end" is disabled) */}
                {!doNotEnd && endDate && endTime && (
                  <div className="flex items-center gap-3">
                    <CalendarIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <p className="text-sm text-foreground">
                      {t('campaigns.paymentrecoveryreview.dialog.end_message', {
                        0: endTime,
                        1: formatCampaignDate(endDate),
                      })}
                    </p>
                  </div>
                )}

                {/* AI Agent info */}
                <div className="flex items-center gap-3">
                  <ZapIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <p className="text-sm text-foreground">
                    {t('campaigns.review.dialog.ai_agent_message', {
                      0: draftAiAgentData?.brain_name || 'Unknown',
                    })}
                  </p>
                </div>

                {/* Monitor performance */}
                <div className="flex items-center gap-3">
                  <ChartNoAxesCombinedIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <p className="text-sm text-foreground">
                    {t('campaigns.review.dialog.monitor_message')}
                  </p>
                </div>
              </div>

              {/* Can still edit info */}
              <div className="rounded-lg border border-border p-4">
                <div className="flex items-start gap-3">
                  <InfoIcon className="mt-0.5 h-4 w-4 shrink-0" />
                  <div className="space-y-1">
                    <p className="text-sm leading-none font-medium">
                      {t('campaigns.review.dialog.can_edit_title')}
                    </p>
                    <p className="text-sm leading-6 text-muted-foreground">
                      {t('campaigns.review.dialog.can_edit_message')}
                    </p>
                  </div>
                </div>
              </div>
            </>
          </DialogDescription>

          <DialogFooter className="gap-3">
            <Button variant="ghost-neutral" onClick={() => setIsConfirmDialogOpen(false)}>
              {t('campaigns.review.dialog.cancel')}
            </Button>
            <Button onClick={handleTrackedConfirmSubmit}>
              {t('campaigns.review.dialog.start_campaign')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
